<template>
  <b-card no-body class="card-container" v-if="ready">
    <!-- HEADER -->
    <template #header>
      <b-row class="mb-2 mt-2">
        <!-- Col gauche (facultatif) : champ de recherche -->
        <b-col md="6">
          <b-input-group class="search-widget simple-search">
            <template #prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search" />
              </b-input-group-text>
            </template>
            <b-form-input
              type="search"
              :placeholder="$t('search_department')"
              v-model="searchQuery"
            />
          </b-input-group>
        </b-col>

        <!-- Col droite : bouton "Create new problem department" -->
        <b-col md="6" class="text-right">
          <b-button :href="createDepartmentRoute()" variant="primary" class="btn-round">
            <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
            {{ $t('Create new problem department') }}
          </b-button>
        </b-col>
      </b-row>
    </template>

    <!-- BODY -->
    <b-card-body>
      <!-- Table -->
      <b-table
        striped
        hover
        responsive="sm"
        class="mb-3"
        id="problem-departments-table"
        ref="problemDepartmentsTable"
        :fields="fields"
        :items="getItems"
        :current-page="pagination.currentPage"
        :busy="loading"
        show-empty
        :empty-text="$t('No departments found')"
      >
        <!-- Contenu vide -->
        <template #empty="scope">
          <div class="lead text-center py-3">
            {{ scope.emptyText }}
          </div>
        </template>

        <!-- Overlay de chargement -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" label="Loading..." />
            <strong>{{ $t("loading") }}</strong>
          </div>
        </template>

        <!-- Colonne : categories -->
        <template #cell(categories)="data">
          {{ data.item.categories ? data.item.categories.length : 0 }}
        </template>

        <!-- Colonne : created_at -->
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <!-- Colonne : updated_at -->
        <template #cell(updated_at)="data">
          {{ formatDate(data.item.updated_at) }}
        </template>

        <!-- Colonne : actions -->
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            :href="editDepartmentRoute(data.item.id)"
          >
            <font-awesome-icon :icon="['fas', 'pen']" />
            {{ $t('Edit') }}
          </b-button>
          <delete-button
            @success="deleteItem(data.item.id)"
            class="ml-2 actions-danger"
            type="problem department"
            :title="`Delete department ${data.item.name}`"
          />
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination.lastPage > 1"
        v-model="pagination.currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.perPage"
        aria-controls="problem-departments-table"
      />
    </b-card-body>

    <!-- FOOTER -->
    <b-card-footer>
      <div class="text-center" v-if="pagination.total > 0">
        {{
          $t("showing_x_results", {
            current: pagination.to - pagination.from + 1,
            total: pagination.total,
          })
        }}
      </div>
    </b-card-footer>
  </b-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import deleteButton from "../Common/DeleteButton";

export default {
  name: "ProblemDepartmentsList",
  components: { deleteButton },
  props: {
    initialDepartments: {
      type: Array,
      required: true
    },
    initialPagination: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Champs de la table
      fields: [
        { key: "name", label: this.$t("Name"), sortable: true },
        { key: "categories", label: this.$t("Categories"), sortable: false },
        { key: "created_at", label: this.$t("Created"), sortable: true },
        { key: "updated_at", label: this.$t("Updated"), sortable: true },
        { key: "actions", label: "", class: "text-right" }
      ],

      // Données
      departments: this.initialDepartments,

      // Pagination
      pagination: {
        total: this.initialPagination.total || 0,
        perPage: this.initialPagination.per_page || 15,
        currentPage: this.initialPagination.current_page || 1,
        lastPage: this.initialPagination.last_page || 1,
        from: this.initialPagination.from || 1,
        to: this.initialPagination.to || 1
      },

      // Recherche
      searchQuery: "",

      // Indicateur de chargement
      loading: false,
      // Montre que le composant est prêt
      ready: true
    };
  },
  mounted() {
    // Si besoin, on peut faire un fetch initial ici
    // this.fetchDepartments();
  },
  methods: {
    /**
     * Provider pour <b-table> (items)
     */
    getItems(ctx, cb) {
      this.pagination.currentPage = ctx.currentPage;
      // Gérer éventuellement ctx.sortBy, ctx.sortDesc pour le tri
      this.fetchDepartments().then(() => {
        cb(this.departments);
      });
      return null;
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    createDepartmentRoute() {
      return `/settings/problem-departments/new`;
    },

    editDepartmentRoute(id) {
      return `/settings/problem-departments/${id}/edit`;
    },

    deleteItem(id) {
      axios
        .delete(`/api/problem-departments/${id}`)
        .then(() => {
          this.$bvToast.toast(this.$t("Department deleted successfully"), {
            title: this.$t("Success"),
            variant: "success",
            solid: true
          });
          this.reload();
        })
        .catch(error => {
          this.$bvToast.toast(this.$t("Failed to delete department"), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true
          });
        });
    },

    /**
     * Recharger la table
     */
    reload() {
      this.$root.$emit("bv::refresh::table", "problem-departments-table");
    },

    /**
     * Fetch de la liste des départements
     */
    fetchDepartments() {
      this.loading = true;
      const params = {
        page: this.pagination.currentPage,
        query: this.searchQuery,
        full: true
      };
      return axios
        .get(`/api/problem-departments`, { params })
        .then(response => {
          this.departments = response.data.data;
          const meta = response.data.meta;

          this.pagination.total = meta.total;
          this.pagination.perPage = meta.per_page;
          this.pagination.currentPage = meta.current_page;
          this.pagination.lastPage = meta.last_page;
          this.pagination.from = meta.from;
          this.pagination.to = meta.to;
        })
        .catch(error => {})
        .finally(() => {
          this.loading = false;
        });
    }
  },
  watch: {
    // Relancer la table dès que la recherche change (optionnel)
    searchQuery(newVal, oldVal) {
      this.pagination.currentPage = 1;
      this.reload();
    }
  }
};
</script>

<style scoped>
.card-container {
  margin-bottom: 1rem;
}

/* Optionnel : overlay loader si tu veux un aspect "blanc" couvrant tout */
.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
</style>
